"use client";

import { Gift, Palette, Sparkles, Type } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Newborn() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = [{
    icon: Type,
    label: "Portable hygiene"
  }, {
    icon: Palette,
    label: "Gentle formula"
  }, {
    icon: Gift,
    label: "Family approved"
  }];
  return <section data-section-id="5662" id="the-newborn-moment" className="dark bg-background py-24 md:py-32">
      <div className="mx-auto max-w-[1240px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {}
          <div ref={leftRef} className="lg:col-span-5 relative transition-all duration-700 ease-out" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(12px)"
        }}>
            <div className="relative overflow-hidden rounded-3xl border border-border/60 shadow-lg">
              <div className="aspect-[3/4] w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/newborn_protection_mist.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/newborn_protection_mist.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/newborn_protection_mist.webp-wvc-sizes" alt="Parent holding a newborn with the B.Y.E. Hygiene Mist nearby" className="h-full w-full object-cover" loading="lazy" decoding="async" />
              </div>
            </div>

            {}
            <div className="absolute bottom-5 left-5 md:bottom-6 md:left-6 bg-card text-card-foreground rounded-lg px-4 py-2.5 shadow-md flex items-center gap-2">
              <Sparkles className="size-4 text-primary" strokeWidth={1.5} />
              <span className="font-default text-sm font-medium tracking-tight">
                Prepared for peace
              </span>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-7 transition-all duration-700 ease-out" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(12px)",
          transitionDelay: rightVisible ? "120ms" : "0ms"
        }}>
            <Badge variant="secondary" className="rounded-lg px-3 py-1 text-xs font-medium tracking-[0.14em] uppercase bg-secondary/25 text-secondary-foreground border-transparent">
              The Newborn Moment
            </Badge>

            <h1 className="font-default font-normal text-foreground mt-5 tracking-tight" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 4.5rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.02em"
          }}>
              Prepared for every{" "}
              <em className="italic text-primary font-normal">new</em> beginning
            </h1>

            <p className="font-default text-muted-foreground mt-6 max-w-[520px] text-lg leading-relaxed">
              Bringing home a newborn is a beautiful, vulnerable time. Our hygiene mist 
              offers a simple, gentle way to maintain cleanliness, giving you the 
              peace of mind to focus on what truly matters: the connection with your baby.
            </p>

            {}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 md:gap-x-5">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <div key={i} data-index={i} className="flex items-center gap-4 md:gap-5">
                    <div className="flex items-center gap-2.5">
                      <Icon className="size-[22px] text-foreground" strokeWidth={1.5} />
                      <span className="font-default text-sm font-medium text-foreground">
                        {feature.label}
                      </span>
                    </div>
                    {i < features.length - 1 && <span aria-hidden="true" className="hidden md:inline-block size-1 rounded-lg bg-border" />}
                  </div>;
            })}
            </div>

            {}
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-7 py-6 h-auto text-base font-medium shadow-md hover:shadow-lg transition-all duration-200">
                <Link to="/ShopPage">
                  <Sparkles className="size-4" strokeWidth={1.75} />
                  Shop the mist
                </Link>
              </Button>

              <Button asChild variant="outline" className="rounded-lg px-7 py-6 h-auto text-base font-medium border-[1.5px] border-foreground text-foreground bg-transparent hover:bg-foreground hover:text-background transition-colors duration-200">
                <Link to="/OurStoryPage">Our story</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}